import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Column widths, shared so dialogs can line up over the card exactly. */
const AUTH_COLUMN_WIDTH = {
  card: "max-w-[27.5rem]",
  wide: "max-w-2xl",
  full: "max-w-4xl",
} as const;

type AuthSize = keyof typeof AUTH_COLUMN_WIDTH;

/**
 * Shared frame for every pre-auth page — the redesign's split-screen hero.
 *
 * Full-bleed photo with a gradient scrim, the brand mark bottom-left, and the
 * form in a card pinned right on desktop / centred on mobile.
 *
 * Card anatomy follows the prototype top to bottom: generous top whitespace,
 * heading, subtitle, form, secondary link ("Create one"), then the brand mark
 * centred at the foot of the card.
 *
 * One deliberate departure: copy is role-neutral. The prototype's card reads
 * "Welcome Back Learner!", but these pages are the front door for every role —
 * learner, manager, admin, business admin, partner and super admin all sign in
 * here. Nothing knows the role until after authentication, so learner-specific
 * wording would be wrong for most users.
 *
 * The prototype's fixed `pt-16` / `pt-24` whitespace is applied only at
 * `size="card"`. It is what gives the sign-in card its proportions, but ours
 * also carry a long Register form and a full terms document, where that much
 * dead space would push content off-screen.
 */
export const AuthShell = ({
  title,
  subtitle,
  children,
  size = "card",
  footer,
  className,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /** card = the standard form width; wide = long forms; full = documents. */
  size?: AuthSize;
  /** Secondary line inside the card, above the brand mark (e.g. "Create one"). */
  footer?: ReactNode;
  className?: string;
}) => {
  const width = AUTH_COLUMN_WIDTH[size];

  // Only the short cards get the prototype's fixed internal whitespace; on a
  // long form or a document it would push content off-screen.
  const roomy = size === "card";

  return (
    <div
      className={cn(
        "relative flex min-h-screen w-full items-start justify-center overflow-hidden bg-zinc-900 lg:justify-end",
        className,
      )}
    >
      {/* Hero */}
      <img
        src="/images/auth-hero.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent lg:bg-linear-to-r lg:from-black/20 lg:via-black/20 lg:to-black/60"
      />

      {/* Brand block — hidden on small screens where the card owns the viewport */}
      <div className="pointer-events-none absolute bottom-8 left-6 z-10 hidden sm:block sm:bottom-10 sm:left-10">
        <img
          src="/logo_w.svg"
          alt={`${import.meta.env.VITE_APP_NAME} logo`}
          className="h-7 w-auto object-contain lg:h-8"
        />
        <p className="mt-2 text-[clamp(0.8rem,1.2vw,1rem)] text-zinc-200">
          Your next skill starts here.
        </p>
      </div>

      {/* Form column — centred while it fits, pinned right from lg up.
          `my-auto` rather than `items-center` on the parent: auto margins
          collapse to zero once the card is taller than the viewport, so a long
          form or the terms document can still be scrolled to from the top. */}
      <div
        className={cn(
          "relative z-10 my-auto flex w-full flex-col items-center gap-4 px-4 py-10 sm:px-6 lg:mr-20 lg:px-0",
          width,
        )}
      >
        <div className="mx-auto w-full rounded-3xl border border-zinc-200 bg-white p-6 shadow-2xl">
          <h1
            className={cn(
              "text-2xl text-zinc-900 sm:text-3xl",
              roomy && "pt-10 sm:pt-16",
            )}
          >
            {title}
          </h1>
          {subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}

          <div className="mt-8">{children}</div>

          {footer && <div className="mt-5 text-center text-sm text-zinc-500">{footer}</div>}

          {/*
            Brand mark sits at the foot of the card, per the redesign. Uses the
            colour lockup: /logo.svg and /logo_w.svg are byte-identical pure
            white, so either would be invisible against the white card.
          */}
          <img
            src="/logo_b.svg"
            alt={`${import.meta.env.VITE_APP_NAME} logo`}
            className={cn(
              "mx-auto h-5 w-auto object-contain",
              roomy ? "mt-16 sm:mt-20" : "mt-10",
            )}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Backdrop + positioning for any dialog raised from an auth page.
 *
 * It mirrors AuthShell's own placement — centred while the viewport is narrow,
 * pinned right from lg up — so a dialog opens over the card instead of drifting
 * into the middle of the hero photo. `size` should match the AuthShell it is
 * raised from, which keeps the dialog centred on the card behind it.
 *
 * Children are centred within that column, so a dialog narrower than the card
 * (most of them) still reads as centred on it.
 */
export const AuthModalOverlay = ({
  size = "card",
  backdropClassName = "bg-black/50",
  onBackdropClick,
  children,
  ...rest
}: {
  size?: AuthSize;
  /** Override just the scrim, e.g. a lighter one for a loading state. */
  backdropClassName?: string;
  onBackdropClick?: () => void;
  children: ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    {...rest}
    className={cn(
      "fixed inset-0 z-50 flex items-center justify-center p-4 lg:justify-end lg:pr-20",
      backdropClassName,
    )}
    onClick={onBackdropClick}
  >
    <div
      className={cn("flex w-full justify-center", AUTH_COLUMN_WIDTH[size])}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

/* ── Field primitives shared by every auth form ────────────────────────── */

/** Vendor auth input: white, zinc-300 border, brand focus ring. */
export const AUTH_INPUT_CLASS =
  "w-full rounded-lg border border-zinc-300 bg-white py-3 pl-4 pr-10 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-700 focus:outline-none focus:ring-1 focus:ring-brand-700 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-400";

/**
 * Right-hand adornment inside a field — the password eye toggle. Pair with the
 * `relative` wrapper AuthField already provides.
 */
export const AUTH_ADORNMENT_CLASS =
  "absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-zinc-400 transition-colors hover:text-zinc-600 disabled:opacity-50";

/** Vendor auth submit button. */
export const AUTH_SUBMIT_CLASS =
  "mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-brand-800 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-70";

/**
 * The redesign's fields carry their label in the placeholder, so the `<label>`
 * defaults to screen-reader-only — visually matching the prototype without
 * leaving the input unlabelled for assistive tech. Long forms (Register) set
 * `showLabel` so fields stay identifiable once they contain a value.
 */
export const AuthField = ({
  label,
  htmlFor,
  hint,
  showLabel = false,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  showLabel?: boolean;
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn("flex flex-col", className)}>
    <label
      htmlFor={htmlFor}
      className={showLabel ? "text-sm font-medium text-zinc-700" : "sr-only"}
    >
      {label}
    </label>
    <div className={cn("relative", showLabel && "mt-1.5")}>{children}</div>
    {hint && <p className="mt-1.5 text-xs text-zinc-400">{hint}</p>}
  </div>
);

/** Standard brand link used across the auth pages. */
export const AuthLink = ({
  className,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a className={cn("font-medium text-brand-700 hover:text-brand-800", className)} {...rest}>
    {children}
  </a>
);
